import React, { useEffect, useLayoutEffect, useState } from 'react'
import { Form, Row, Col, Select, InputNumber } from 'antd'
import useAddress from 'components/Address/hooks/useAddress'
import RefinancingButton from '../RefinancingButton/RefinancingButton'
import { IAddress } from 'egi/types'
import { Input } from 'components'
import { useResources, useSimulation } from 'hooks'
import { useDispatch } from 'react-redux'
import { resourcesGetStates, resourcesGetStatesIBGE } from 'store/modules/resources/actions'
import { simulation as consumerSimulation } from 'egi/consumers'
import { simulationLastStep, simulationValues } from 'store/modules/simulation/actions'
import swal from 'utils/swal'

interface IAddressDataValues {
  zipcode: string
  state: string
  city: string
  neighborhood: string
  street: string
  number: number
  additional: string
}

export default function AddressData () {
  const [formRef] = Form.useForm()
  const simulation = useSimulation()
  const dispatch = useDispatch()
  const resources = useResources()
  const { getAddress, updateCities, cities } = useAddress()
  const [submitLoading, setSubmitLoading] = useState(false)

  async function updateAddress (cep: string, remainingAddress?: Partial<IAddress>) {
    const addressValues = await getAddress({ cep })
    if (formRef) formRef.setFieldsValue({ ...addressValues, ...remainingAddress })
  }

  const onChangeCep = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    const formated = value.replace(/\D/g, '')

    if (formated.length === 8) {
      updateAddress(value)
    }
  }

  useEffect(() => {
    const { zipcode } = simulation.values || {}

    const remainingAddress = {
      state: simulation.values?.ufLabel,
      city: simulation.values?.cityLabel,
      number: simulation.values?.addressNumber
    }

    if (zipcode) updateAddress(String(zipcode), remainingAddress)
  }, [simulation.values])

  useEffect(() => {
    async function fillCity () {
      const findUF = resources.ibgeStates.find((element) => element.label === simulation.values?.ufLabel)
      if (findUF) updateCities(findUF.cities)
    }

    fillCity()
  }, [resources.ibgeStates, simulation.values?.ufLabel, formRef])

  useLayoutEffect(() => {
    if (resources.states.length < 1) dispatch(resourcesGetStates())
    if (resources.ibgeStates.length < 1) dispatch(resourcesGetStatesIBGE())
  }, [resources.states, resources.ibgeStates])

  const onSubmit = async (values: IAddressDataValues) => {
    const uf = resources.ibgeStates.find((element) => element.label === values.state)?.value
    const city = resources.ibgeStates.find((element) => element.label === values.state)?.cities.find((c) => c.label === values.city)?.value

    const data = {
      ...simulation.values,
      zipcode: values.zipcode.replace('-', ''),
      uf,
      ufLabel: values.state,
      city,
      cityLabel: values.city,
      addressNumber: values.number,
      currentStep: 6
    }

    setSubmitLoading(true)
    try {
      const response = await consumerSimulation.simulation(data)
      dispatch(simulationLastStep(response.data.simulation))
      dispatch(simulationValues({ iof: response.data.simulation.iof, ...simulation.values }, 4))
    } catch (err: any) {
      swal.htmlBasic({
        title: 'Atenção',
        text: `
          <h2>${err.message}</h2>
          ${
            err.data?.invalid?.length
              ? `<ul style="text-align: left; margin-top: 16px;">
                  ${err.data.invalid
                    .map((item: { message: string }) => `<li>${item.message}</li>`)
                    .join('')}
                </ul>`
              : ''
          }
        `,
        icon: 'warning'
      })
    } finally {
      setSubmitLoading(false)
    }
  }

  return (
    <div className='refinancing-content'>
      <h4 className="simulator-title-baseline" style={{ fontSize: 18, marginBottom: 24 }}>Endereço</h4>

      <Form
        form={formRef}
        layout="vertical"
        onFinish={onSubmit}
      >
        <Row gutter={[16, 16]}>
          <Col className="py-1" lg={24} md={24} sm={24} xs={24}>
            <Form.Item
              name='zipcode'
              label={<label className='simulator-label'>CEP</label>}
              className='refinancing-form-item'
              rules={[{ required: true, message: 'Digite um CEP válido' }]}
              required
            >
              <Input
                placeholder="Digite o CEP"
                className='simulator-input ant-input'
                mask='cep'
                onBlur={(e) => { e.target.value && updateAddress(e.target.value) }}
                onChange={onChangeCep}
              />
            </Form.Item>
          </Col>

          <Col className="py-1" lg={24} md={24} sm={24} xs={24}>
            <Form.Item
              name='state'
              label={<label className='simulator-label'>UF</label>}
              className='refinancing-form-item'
              rules={[{ required: true, message: 'Selecione um UF válido' }]}
              required
            >
              <Select
                className="unauth-inputs-center"
                showSearch
                placeholder='UF'
                optionFilterProp="children"
                onChange={(value) => {
                  if (resources.ibgeStates.length > 0) {
                    const findUF: any = resources.ibgeStates.find(element => element.label === value)
                    if (findUF) {
                      formRef.setFieldsValue({ city: undefined })
                      updateCities(findUF.cities)
                    }
                  }
                }}
              >
                {resources.states.map(state =>
                  <Select.Option key={state} value={state}>{state}</Select.Option>
                )}
              </Select>
            </Form.Item>
          </Col>

          <Col className="py-1" lg={24} md={24} sm={24} xs={24}>
            <Form.Item
              name='city'
              label={<label className='simulator-label'>Município</label>}
              className='refinancing-form-item'
              rules={[{ required: true, message: 'Selecione um município válido' }]}
              required
            >
              <Select
                className="unauth-inputs-center"
                showSearch
                placeholder="Digite seu município"
                optionFilterProp="children">
                {cities.map(state =>
                  <Select.Option
                    key={state.value}
                    value={state.label}
                  >
                    {state.label}
                  </Select.Option>
                )}
              </Select>
            </Form.Item>
          </Col>

          <Col className="py-1" lg={24} md={24} sm={24} xs={24}>
            <Form.Item
              name='neighborhood'
              label={<label className='simulator-label'>Bairro</label>}
              className='refinancing-form-item'
              rules={[{ required: true, message: 'Digite um bairro válido' }]}
              required
            >
              <Input placeholder="Digite o bairro" className='simulator-input ant-input' />
            </Form.Item>
          </Col>

          <Col className="py-1" lg={24} md={24} sm={24} xs={24}>
            <Form.Item
              name='street'
              label={<label className='simulator-label'>Logradouro</label>}
              className='refinancing-form-item'
              rules={[{ required: true, message: 'Digite um logradouro válido' }]}
              required
            >
              <Input placeholder="Digite o logradouro" className='simulator-input ant-input'
              />
            </Form.Item>
          </Col>

          <Col className="py-1" lg={24} md={24} sm={24} xs={24}>
            <Form.Item
              name='number'
              label={<label className='simulator-label'>Número</label>}
              className='refinancing-form-item'
              rules={[{ required: true, message: 'Digite um número válido' }]}
              required
            >
              <InputNumber
                type='number'
                min={0}
                placeholder='Digite o número'
                className='simulator-input ant-input w-100'
              />
            </Form.Item>
          </Col>

          <Col className="py-1" lg={24} md={24} sm={24} xs={24}>
            <Form.Item
              name='additional'
              label={<label className='simulator-label'>Complemento</label>}
              className='refinancing-form-item'
            >
              <Input placeholder="Digite o complemento" className='simulator-input ant-input' />
            </Form.Item>
          </Col>
        </Row>

        <RefinancingButton
          handleClick={() => formRef?.submit()}
          loading={submitLoading}
        />
      </Form>
    </div >
  )
}
